import React, { useState, useEffect } from 'react';
import { Save, Settings, Database, Shield, Globe, Bell } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    site: {
      name: 'LeaserTech',
      description: 'Elektronik ürünler için güvenilir alışveriş',
      currency: 'TRY',
      language: 'tr'
    },
    security: {
      sessionTimeout: 24,
      maxLoginAttempts: 5,
      passwordMinLength: 8,
      requireEmailVerification: true,
      twoFactorAuth: false
    },
    notifications: {
      newOrderEmail: true,
      lowStockAlert: true,
      userRegistrationEmail: true,
      orderStatusEmail: true
    },
    shipping: {
      freeShippingThreshold: 300,
      defaultShippingCost: 50,
      expressShippingCost: 249,
      estimatedDeliveryDays: 3
    }
  });

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Ayarları yükle
  const loadSettings = async () => {
    try {
      setInitialLoading(true);
      const response = await api.get('/settings');
      setSettings(response.data);
      
      // localStorage'a kaydet
      localStorage.setItem('adminSettings', JSON.stringify(response.data));
    } catch (error) {
      console.error('Ayarlar yüklenemedi:', error);
      toast.error('Ayarlar yüklenemedi');
      
      // localStorage'dan yükle
      const savedSettings = localStorage.getItem('adminSettings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } finally {
      setInitialLoading(false);
    }
  };

  // Sayfa yüklendiğinde ayarları getir
  useEffect(() => {
    loadSettings();
  }, []);

  const handleInputChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await api.put('/settings', settings);
      toast.success('Ayarlar başarıyla kaydedildi');
      
      // localStorage'a kaydet
      localStorage.setItem('adminSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Ayarlar kaydedilemedi:', error);
      toast.error('Ayarlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Ayarlar yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Sistem Ayarları</h1>
        <p className="mt-2 text-gray-600">Sistem genel ayarlarını yönetin</p>
      </div>

      <div className="space-y-8">
        {/* Site Ayarları */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Globe className="h-5 w-5 text-blue-600 mr-2" />
              Site Ayarları
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Site Adı
                </label>
                <input
                  type="text"
                  value={settings.site.name}
                  onChange={(e) => handleInputChange('site', 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Para Birimi
                </label>
                <select
                  value={settings.site.currency}
                  onChange={(e) => handleInputChange('site', 'currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="TRY">TRY (₺)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Açıklaması
              </label>
              <textarea
                value={settings.site.description}
                onChange={(e) => handleInputChange('site', 'description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>


        {/* Güvenlik Ayarları */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Shield className="h-5 w-5 text-red-600 mr-2" />
              Güvenlik Ayarları
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Oturum Süresi (Saat)
                </label>
                <input
                  type="number"
                  value={settings.security.sessionTimeout}
                  onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maksimum Giriş Denemesi
                </label>
                <input
                  type="number"
                  value={settings.security.maxLoginAttempts}
                  onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.security.requireEmailVerification}
                  onChange={(e) => handleInputChange('security', 'requireEmailVerification', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">E-posta doğrulama zorunlu</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.security.twoFactorAuth}
                  onChange={(e) => handleInputChange('security', 'twoFactorAuth', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">İki faktörlü kimlik doğrulama</span>
              </label>
            </div>
          </div>
        </div>

        {/* Bildirim Ayarları */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Bell className="h-5 w-5 text-yellow-600 mr-2" />
              Bildirim Ayarları
            </h3>
          </div>
          <div className="p-6 space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.newOrderEmail}
                onChange={(e) => handleInputChange('notifications', 'newOrderEmail', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Yeni sipariş e-postası</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.lowStockAlert}
                onChange={(e) => handleInputChange('notifications', 'lowStockAlert', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Düşük stok uyarısı</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.userRegistrationEmail}
                onChange={(e) => handleInputChange('notifications', 'userRegistrationEmail', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Kullanıcı kayıt e-postası</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.notifications.orderStatusEmail}
                onChange={(e) => handleInputChange('notifications', 'orderStatusEmail', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Sipariş durumu e-postası</span>
            </label>
          </div>
        </div>

        {/* Kargo Ayarları */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Database className="h-5 w-5 text-purple-600 mr-2" />
              Kargo Ayarları
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ücretsiz Kargo Eşiği (₺)
                </label>
                <input
                  type="number"
                  value={settings.shipping.freeShippingThreshold}
                  onChange={(e) => handleInputChange('shipping', 'freeShippingThreshold', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Varsayılan Kargo Ücreti (₺)
                </label>
                <input
                  type="number"
                  value={settings.shipping.defaultShippingCost}
                  onChange={(e) => handleInputChange('shipping', 'defaultShippingCost', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hızlı Kargo Ücreti (₺)
                </label>
                <input
                  type="number"
                  value={settings.shipping.expressShippingCost}
                  onChange={(e) => handleInputChange('shipping', 'expressShippingCost', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tahmini Teslimat Süresi (Gün)
                </label>
                <input
                  type="number"
                  value={settings.shipping.estimatedDeliveryDays}
                  onChange={(e) => handleInputChange('shipping', 'estimatedDeliveryDays', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Kaydet Butonu */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;

